<template>
  <v-dialog v-model="dialogVisible" max-width="700px" persistent>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Editeaza Client</span>
        <v-btn icon variant="text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text v-if="loading" class="text-center pa-8">
        <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
        <div class="mt-4">Se incarca datele clientului...</div>
      </v-card-text>

      <v-card-text v-else>
        <v-form ref="form" @submit.prevent="saveClient">
          <!-- White-Label / Branding Section (for distributors and root client) - FIRST/ROOT section -->
          <v-card v-if="showWhiteLabelSection" variant="outlined" class="mb-4">
            <v-card-title class="text-subtitle-1 bg-blue-darken-1 text-white">
              <v-icon class="mr-2">mdi-palette</v-icon>
              Setari White-Label / Branding
            </v-card-title>
            <v-card-text>
              <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                Aceste setari permit personalizarea aplicatiei pentru subclientii dvs.
                Logo-ul si numele brandului vor fi afisate in NavBar pentru toti subclientii.
              </v-alert>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.brand_name"
                    label="Nume Brand"
                    hint="Numele care va aparea in aplicatie pentru subclienti"
                    persistent-hint
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.brand_name = ''"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.logo_url"
                    label="URL Logo"
                    hint="Link catre imaginea logo (PNG, JPG - max 120px inaltime)"
                    persistent-hint
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.logo_url = ''"
                  ></v-text-field>
                </v-col>
              </v-row>
              <!-- Logo Upload Section -->
              <v-row>
                <v-col cols="12">
                  <div class="d-flex align-center gap-2">
                    <v-file-input
                      v-model="selectedLogoFile"
                      label="Incarca logo (PNG, JPG)"
                      prepend-icon="mdi-image"
                      accept="image/png,image/jpeg,image/gif"
                      variant="outlined"
                      density="compact"
                      hide-details
                      class="flex-grow-1"
                      style="max-width: 300px;"
                    ></v-file-input>
                    <v-btn
                      color="primary"
                      variant="elevated"
                      :loading="uploadingLogo"
                      :disabled="!selectedLogoFile"
                      @click="uploadLogo"
                    >
                      <v-icon class="mr-1">mdi-upload</v-icon>
                      Incarca
                    </v-btn>
                    <v-btn
                      v-if="form.logo_url"
                      color="error"
                      variant="text"
                      size="small"
                      @click="form.logo_url = ''"
                    >
                      <v-icon>mdi-delete</v-icon>
                      Sterge
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
              <v-row v-if="form.logo_url">
                <v-col cols="12">
                  <div class="text-caption mb-2">Preview Logo:</div>
                  <div class="logo-preview pa-4 rounded" style="background: #1976d2;">
                    <img :src="form.logo_url" alt="Logo Preview" class="preview-img" @error="onLogoError" />
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Basic Info Section -->
          <v-card variant="outlined" class="mb-4">
            <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
              <v-icon class="mr-2">mdi-account</v-icon>
              Informatii de Baza
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.name"
                    label="Nume Client *"
                    :rules="[rules.required]"
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.name = ''"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.short_name"
                    label="Nume Scurt"
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.short_name = ''"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.phone"
                    label="Telefon"
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.phone = ''"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.email"
                    label="Email"
                    :rules="[rules.email]"
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.email = ''"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.notification_email"
                    label="Email Notificari"
                    :rules="[rules.email]"
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.notification_email = ''"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Business Settings Section -->
          <v-card variant="outlined" class="mb-4">
            <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
              <v-icon class="mr-2">mdi-cog</v-icon>
              Setari Client
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.client_type_id"
                    :items="clientTypes"
                    item-title="title"
                    item-value="id"
                    label="Tip Client"
                    variant="outlined"
                    density="compact"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.billing_type_id"
                    :items="billingTypes"
                    item-title="title"
                    item-value="id"
                    label="Tip Facturare"
                    variant="outlined"
                    density="compact"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.legal_type"
                    :items="legalTypes"
                    item-title="name"
                    item-value="id"
                    label="Tip Juridic"
                    variant="outlined"
                    density="compact"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6" v-if="form.billing_type_id === 2">
                  <v-text-field
                    v-model.number="form.max_credit"
                    label="Credit Maxim"
                    type="number"
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.max_credit = null"
                  ></v-text-field>
                </v-col>
              </v-row>
              <!-- Admin-only: Status Client -->
              <v-row v-if="isAdmin">
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.client_status_id"
                    :items="clientStatuses"
                    item-title="title"
                    item-value="id"
                    label="Status Client"
                    variant="outlined"
                    density="compact"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :model-value="parentClientName"
                    label="Client Parinte"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-switch
                    v-model="form.is_distributor"
                    label="Este Distribuitor"
                    color="primary"
                    density="compact"
                    hide-details
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    v-model="form.visible"
                    label="Vizibil"
                    color="success"
                    density="compact"
                    hide-details
                  ></v-switch>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Financial Info Section (Read-only) -->
          <v-card variant="outlined" class="mb-4">
            <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
              <v-icon class="mr-2">mdi-cash</v-icon>
              Informatii Financiare (doar citire)
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    :model-value="readonlyFields.current_debit"
                    label="Debit Curent"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    :model-value="readonlyFields.current_credit"
                    label="Credit Curent"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    :model-value="readonlyFields.max_credit"
                    label="Credit Maxim"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    :model-value="readonlyFields.personal_volume"
                    label="Volum Personal"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :model-value="readonlyFields.group_volume"
                    label="Volum Grup"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- System Info Section (Read-only, Admin only) -->
          <v-card v-if="isAdmin" variant="outlined" class="mb-4">
            <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
              <v-icon class="mr-2">mdi-information</v-icon>
              Informatii Sistem (doar citire)
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    :model-value="readonlyFields.id"
                    label="ID Client"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    :model-value="readonlyFields.app_id"
                    label="App ID"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    :model-value="readonlyFields.veridian_distributor_client_id"
                    label="Veridian Distributor ID"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    :model-value="readonlyFields.created_at"
                    label="Data Creare"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :model-value="readonlyFields.created_by_user_name"
                    label="Creat de"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    :model-value="readonlyFields.updated_at"
                    label="Ultima Actualizare"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :model-value="readonlyFields.mlm_rank_name"
                    label="Rang MLM"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row v-if="readonlyFields.deleted">
                <v-col cols="12" md="4">
                  <v-text-field
                    model-value="Da"
                    label="Sters"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                    bg-color="error"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    :model-value="readonlyFields.deleted_at"
                    label="Data Stergere"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    :model-value="readonlyFields.deleted_by_user_name"
                    label="Sters de"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Company Details Section (only for legal_type = 2) -->
          <v-card v-if="form.legal_type === 2" variant="outlined" class="mb-4">
            <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
              <v-icon class="mr-2">mdi-domain</v-icon>
              Detalii Companie
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.company_name"
                    label="Denumire Companie"
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.company_name = ''"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.cui"
                    label="CUI"
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.cui = ''"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.registration_number"
                    label="Nr. Inregistrare (J...)"
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.registration_number = ''"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.bank"
                    label="Banca"
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.bank = ''"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.iban"
                    label="IBAN"
                    variant="outlined"
                    density="compact"
                    @click="$event.target.select()"
                    @dblclick="form.iban = ''"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4 pt-0">
        <!-- Impersonation Button (left side) -->
        <v-btn
          v-if="canImpersonate"
          color="warning"
          variant="elevated"
          :loading="loadingImpersonationUsers"
          @click="openImpersonationDialog"
        >
          <v-icon class="mr-1">mdi-account-switch</v-icon>
          Login ca acest client
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeDialog">Anuleaza</v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="saving"
          :disabled="loading"
          @click="saveClient"
        >
          Salveaza
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Impersonation User Selection Dialog -->
    <v-dialog v-model="showImpersonationDialog" max-width="500">
      <v-card>
        <v-card-title class="bg-warning">
          <v-icon class="mr-2">mdi-account-switch</v-icon>
          Selecteaza userul pentru login
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Selecteaza userul din clientul <strong>{{ impersonationClientName }}</strong>
            ca care vrei sa te autentifici intr-un tab nou.
          </v-alert>

          <v-list v-if="impersonationUsers.length > 0">
            <v-list-item
              v-for="user in impersonationUsers"
              :key="user.id"
              @click="impersonateUser(user)"
              :disabled="generatingToken"
            >
              <template v-slot:prepend>
                <v-icon>mdi-account</v-icon>
              </template>
              <v-list-item-title>{{ user.name || user.username }}</v-list-item-title>
              <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
              <template v-slot:append>
                <v-icon color="warning">mdi-open-in-new</v-icon>
              </template>
            </v-list-item>
          </v-list>

          <v-alert v-else type="warning" variant="tonal">
            Nu exista useri activi in acest client.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showImpersonationDialog = false">Anuleaza</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import { getClientsGeneralSettings } from "@/composables/useGetGeneralSettings";
import { BASE_URL } from "@/composables/url_config";
import { getClientUsersForImpersonation, generateImpersonationToken, openImpersonationTab } from "@/composables/useImpersonation";
import store from "@/store";

export default defineComponent({
  name: 'EditClientDialog',
  props: {
    // v-model for dialog visibility
    modelValue: {
      type: Boolean,
      default: false
    },
    // Client ID to edit
    clientId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['update:modelValue', 'saved', 'close'],
  data() {
    return {
      loading: false,
      saving: false,
      // Logo upload
      selectedLogoFile: null,
      uploadingLogo: false,
      // Impersonation
      showImpersonationDialog: false,
      loadingImpersonationUsers: false,
      generatingToken: false,
      impersonationUsers: [],
      impersonationClientName: '',
      form: {
        name: '',
        short_name: '',
        phone: '',
        email: '',
        notification_email: '',
        client_type_id: null,
        billing_type_id: null,
        legal_type: null,
        max_credit: null,
        is_distributor: false,
        visible: true,
        client_status_id: null,
        // White-label / Branding
        brand_name: '',
        logo_url: '',
        // Company details
        company_name: '',
        registration_number: '',
        cui: '',
        bank: '',
        iban: ''
      },
      // Settings from API
      clientTypes: [],
      billingTypes: [],
      clientStatuses: [],
      parentClientName: '',
      clientTypeName: '',
      // Read-only system fields
      readonlyFields: {
        id: null,
        current_debit: null,
        current_credit: null,
        max_credit: null,
        personal_volume: null,
        group_volume: null,
        app_id: '',
        veridian_distributor_client_id: null,
        created_at: '',
        updated_at: '',
        created_by_user_name: '',
        mlm_rank_name: '',
        deleted: false,
        deleted_at: '',
        deleted_by_user_name: ''
      },
      legalTypes: [
        { id: 1, name: 'Persoana Fizica' },
        { id: 2, name: 'Persoana Juridica' }
      ],
      rules: {
        required: v => !!v || 'Camp obligatoriu',
        email: v => !v || /.+@.+\..+/.test(v) || 'Email invalid'
      }
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    token() {
      return store.getters.getToken;
    },
    isAdmin() {
      const roles = store.getters.getRoles || [];
      return roles.includes('root') || roles.includes('default_admin_roles_l0');
    },
    currentUser() {
      return store.getters.getUser;
    },
    currentUserClientId() {
      return this.currentUser?.client_id || this.currentUser?.client?.id;
    },
    currentUserRoles() {
      return store.getters.getRoles || [];
    },
    canImpersonate() {
      // Check if user has required role
      const roles = this.currentUserRoles;
      const allowedRoles = ['root', 'admin', 'manage_subclients', 'standard_admin_l0'];
      const hasRole = allowedRoles.some(role => roles.includes(role));

      // Cannot impersonate own client
      const isNotOwnClient = this.currentUserClientId !== parseInt(this.clientId);

      console.log('=== canImpersonate check ===');
      console.log('roles:', roles);
      console.log('hasRole:', hasRole);
      console.log('currentUserClientId:', this.currentUserClientId);
      console.log('clientId:', this.clientId);
      console.log('isNotOwnClient:', isNotOwnClient);
      console.log('result:', hasRole && isNotOwnClient && this.clientId);

      // Must have role and not be own client
      return hasRole && isNotOwnClient && this.clientId;
    },
    showWhiteLabelSection() {
      // Show white-label section for distributors OR root clients
      return this.form.is_distributor || this.clientTypeName === 'root';
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.clientId) {
          this.selectedLogoFile = null; // Reset logo file input
          this.loadClientData();
        }
      }
    },
    clientId: {
      handler(newVal) {
        if (newVal && this.modelValue) {
          this.selectedLogoFile = null; // Reset logo file input
          this.loadClientData();
        }
      }
    }
  },
  methods: {
    async uploadLogo() {
      if (!this.selectedLogoFile) return;

      this.uploadingLogo = true;
      try {
        const formData = new FormData();
        formData.append('logo', this.selectedLogoFile);
        formData.append('client_id', this.clientId);

        const response = await fetch(`${BASE_URL}/home/upload_client_logo_api`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.token}`
          },
          body: formData
        });

        const data = await response.json();

        if (data.error) {
          alert(data.message || 'Eroare la incarcarea logo-ului');
          return;
        }

        // Update the logo_url in the form
        this.form.logo_url = data.logo_url;
        this.selectedLogoFile = null;
        log('Logo uploaded successfully: ' + data.logo_url);
      } catch (error) {
        console.error('Error uploading logo:', error);
        alert('Eroare la incarcarea logo-ului');
      } finally {
        this.uploadingLogo = false;
      }
    },
    async loadClientData() {
      this.loading = true;
      try {
        // Load general settings for dropdowns
        const settings = await getClientsGeneralSettings();
        this.clientTypes = settings.client_types || [];
        this.billingTypes = settings.client_billing_types || [];
        this.clientStatuses = settings.client_statuses || [];

        // Load client data
        const response = await fetch(
          `${BASE_URL}/home/get_client_for_edit_api?client_id=${this.clientId}`,
          {
            headers: {
              'Authorization': `Bearer ${this.token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        const data = await response.json();

        if (data.error) {
          alert(data.message || 'Eroare la incarcarea datelor clientului');
          this.closeDialog();
          return;
        }

        // Populate form with client data
        const client = data.client;
        this.form = {
          name: client.name || '',
          short_name: client.short_name || '',
          phone: client.phone || '',
          email: client.email || '',
          notification_email: client.notification_email || '',
          client_type_id: client.client_type_id,
          billing_type_id: client.billing_type_id,
          legal_type: client.legal_type,
          max_credit: client.max_credit,
          is_distributor: client.is_distributor || false,
          visible: client.visible !== false,
          client_status_id: client.client_status_id,
          // White-label / Branding
          brand_name: client.brand_name || '',
          logo_url: client.logo_url || '',
          // Company details
          company_name: client.company_name || '',
          registration_number: client.registration_number || '',
          cui: client.cui || '',
          bank: client.bank || '',
          iban: client.iban || ''
        };
        this.parentClientName = client.parent_client_name || '';
        this.clientTypeName = client.client_type_name || '';

        // Populate read-only system fields
        this.readonlyFields = {
          id: client.id,
          current_debit: client.current_debit,
          current_credit: client.current_credit,
          max_credit: client.max_credit,
          personal_volume: client.personal_volume,
          group_volume: client.group_volume,
          app_id: client.app_id || '',
          veridian_distributor_client_id: client.veridian_distributor_client_id,
          created_at: client.created_at,
          updated_at: client.updated_at,
          created_by_user_name: client.created_by_user_name || '',
          mlm_rank_name: client.mlm_rank_name || '',
          deleted: client.deleted || false,
          deleted_at: client.deleted_at || '',
          deleted_by_user_name: client.deleted_by_user_name || ''
        };
      } catch (err) {
        log("Error loading client data:", err);
        alert('Eroare la incarcarea datelor clientului');
        this.closeDialog();
      } finally {
        this.loading = false;
      }
    },
    async saveClient() {
      // Validate form
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;

      this.saving = true;
      try {
        const response = await fetch(
          `${BASE_URL}/home/update_client_api`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${this.token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              client_id: this.clientId,
              ...this.form
            })
          }
        );
        const data = await response.json();

        if (data.error) {
          alert(data.message || 'Eroare la salvarea clientului');
          return;
        }

        this.$emit('saved', { clientId: this.clientId });
        this.closeDialog();
      } catch (err) {
        log("Error saving client:", err);
        alert('Eroare la salvarea clientului');
      } finally {
        this.saving = false;
      }
    },
    onLogoError(event) {
      event.target.style.display = 'none';
    },
    closeDialog() {
      this.dialogVisible = false;
      this.$emit('close');
      // Reset form
      this.form = {
        name: '',
        short_name: '',
        phone: '',
        email: '',
        notification_email: '',
        client_type_id: null,
        billing_type_id: null,
        legal_type: null,
        max_credit: null,
        is_distributor: false,
        visible: true,
        client_status_id: null,
        // White-label / Branding
        brand_name: '',
        logo_url: '',
        // Company details
        company_name: '',
        registration_number: '',
        cui: '',
        bank: '',
        iban: ''
      };
      this.parentClientName = '';
      this.clientTypeName = '';
      this.readonlyFields = {
        id: null,
        current_debit: null,
        current_credit: null,
        max_credit: null,
        personal_volume: null,
        group_volume: null,
        app_id: '',
        veridian_distributor_client_id: null,
        created_at: '',
        updated_at: '',
        created_by_user_name: '',
        mlm_rank_name: '',
        deleted: false,
        deleted_at: '',
        deleted_by_user_name: ''
      };
      // Reset impersonation state
      this.showImpersonationDialog = false;
      this.impersonationUsers = [];
      this.impersonationClientName = '';
    },
    // Impersonation methods
    async openImpersonationDialog() {
      this.loadingImpersonationUsers = true;
      try {
        const result = await getClientUsersForImpersonation(this.token, this.clientId);
        if (result.error) {
          alert(result.message || 'Eroare la incarcarea userilor');
          return;
        }
        this.impersonationUsers = result.users || [];
        this.impersonationClientName = result.client_name || '';
        this.showImpersonationDialog = true;
      } catch (err) {
        log("Error loading impersonation users:", err);
        alert('Eroare la incarcarea userilor');
      } finally {
        this.loadingImpersonationUsers = false;
      }
    },
    async impersonateUser(user) {
      this.generatingToken = true;
      try {
        const result = await generateImpersonationToken(this.token, user.id);
        if (result.error) {
          alert(result.message || 'Eroare la generarea tokenului');
          return;
        }
        // Open new tab with impersonation URL
        openImpersonationTab(result.token);
        this.showImpersonationDialog = false;
      } catch (err) {
        log("Error generating impersonation token:", err);
        alert('Eroare la generarea tokenului');
      } finally {
        this.generatingToken = false;
      }
    }
  }
});
</script>

<style scoped>
.logo-preview {
  display: inline-block;
  max-width: 300px;
  border: 1px solid rgba(255,255,255,0.3);
}
.preview-img {
  max-height: 60px;
  max-width: 100%;
  object-fit: contain;
}
</style>
